import { supabase } from "@/integrations/supabase/client";
import { StockItem } from "@/lib/accounting/types";
import { StockMovement, MovementType, FIFOLayer, fifoConsume, fifoValuation } from "./types";

export function isStockIn(type: MovementType | string): boolean {
  const norm = String(type).trim().toLowerCase();
  return (
    norm === "in" ||
    norm === "purchase" ||
    norm === "sales return" ||
    norm === "adjustment-in" ||
    norm === "transfer-in" ||
    norm === "opening"
  );
}

export function isStockOut(type: MovementType | string): boolean {
  const norm = String(type).trim().toLowerCase();
  return (
    norm === "out" ||
    norm === "sales" ||
    norm === "purchase return" ||
    norm === "adjustment-out" ||
    norm === "transfer-out"
  );
}

export interface StockCalculationResult {
  currentQty: number;
  currentRate: number;
  currentValue: number;
  totalInQty: number;
  totalOutQty: number;
  fifoValuation: number;
  layers: FIFOLayer[];
}

/**
 * Calculates stock balance and valuation using Weighted Average Cost and FIFO layers
 */
export function calculateStock(
  openingQty: number,
  openingRate: number,
  movements: StockMovement[]
): StockCalculationResult {
  // Sort movements chronologically (date, then createdAt)
  const sorted = [...movements].sort((a, b) => {
    const dComp = (a.date || "").localeCompare(b.date || "");
    if (dComp !== 0) return dComp;
    return (a.createdAt || "").localeCompare(b.createdAt || "");
  });

  let curQty = Math.max(0, openingQty || 0);
  let curVal = curQty * (openingRate || 0);
  let curRate = curQty > 0 ? curVal / curQty : (openingRate || 0);

  let totalIn = curQty;
  let totalOut = 0;

  // Initialize FIFO layers with opening stock if present
  let layers: FIFOLayer[] = [];
  if (curQty > 0) {
    layers.push({
      date: sorted[0]?.date || new Date().toISOString().split("T")[0],
      quantity: curQty,
      rate: openingRate || 0,
    });
  }

  for (const m of sorted) {
    const qty = Number(m.quantity) || 0;
    const rate = Number(m.rate) || 0;

    if (isStockIn(m.type)) {
      totalIn += qty;
      const newQty = curQty + qty;
      const newVal = curVal + qty * rate;
      curRate = newQty > 0 ? newVal / newQty : rate;
      curQty = newQty;
      curVal = newVal;

      layers.push({
        batchId: m.batchId,
        date: m.date,
        quantity: qty,
        rate: rate,
      });
    } else if (isStockOut(m.type)) {
      totalOut += qty;
      curQty = Math.max(0, curQty - qty);
      curVal = curQty * curRate;

      layers = fifoConsume(layers, qty).remaining;
    }
  }

  // Round currency to 2 decimals
  curQty = Math.round(curQty * 100) / 100;
  curRate = Math.round(curRate * 100) / 100;
  curVal = Math.round(curVal * 100) / 100;

  const fifoVal = Math.round(fifoValuation(layers) * 100) / 100;

  return {
    currentQty: curQty,
    currentRate: curRate,
    currentValue: curVal,
    totalInQty: Math.round(totalIn * 100) / 100,
    totalOutQty: Math.round(totalOut * 100) / 100,
    fifoValuation: fifoVal,
    layers,
  };
}

/**
 * Recalculates and updates an inventory item's balances in the MySQL database.
 */
export async function syncItemStockToDb(
  itemId: string,
  companyId: string
): Promise<StockCalculationResult | null> {
  try {
    // 1. Fetch item definition
    const { data: item, error: itemErr } = await supabase
      .from("inventory_items")
      .select("*")
      .eq("id", itemId)
      .maybeSingle();

    if (itemErr || !item) {
      console.warn("[InventoryEngine] Item not found for sync:", itemId);
      return null;
    }

    if (item.item_type === "service") {
      // Services never carry physical stock
      await supabase
        .from("inventory_items")
        .update({
          opening_qty: 0,
          current_qty: 0,
          opening_value: 0,
          current_value: 0,
          reorder_level: null,
          updated_at: new Date().toISOString(),
        })
        .eq("id", itemId);

      return {
        currentQty: 0,
        currentRate: Number(item.standard_rate || 0),
        currentValue: 0,
        totalInQty: 0,
        totalOutQty: 0,
        fifoValuation: 0,
        layers: [],
      };
    }

    // 2. Fetch all movements for this item
    let movQuery = supabase
      .from("stock_movements")
      .select("*")
      .eq("stock_item_id", itemId);

    if (companyId) {
      movQuery = movQuery.eq("company_id", companyId);
    }

    const { data: movRows, error: movErr } = await movQuery;
    if (movErr) throw movErr;

    const movements: StockMovement[] = (movRows || []).map((r: any) => ({
      id: r.id,
      companyId: r.company_id,
      stockItemId: r.stock_item_id,
      voucherId: r.voucher_id,
      voucherLineId: r.voucher_line_id,
      branchId: r.branch_id,
      warehouse: r.warehouse,
      batchId: r.batch_id,
      type: r.type as MovementType,
      date: r.date,
      quantity: Number(r.quantity || 0),
      rate: Number(r.rate || 0),
      value: Number(r.value || 0),
      reference: r.reference,
      narration: r.narration,
      createdBy: r.created_by || "system",
      createdAt: r.created_at,
    }));

    const calc = calculateStock(
      Number(item.opening_qty || 0),
      Number(item.opening_rate || item.cost_rate || 0),
      movements
    );

    // 3. Persist recalculated stock to MySQL
    const now = new Date().toISOString();
    await supabase
      .from("inventory_items")
      .update({
        current_qty: calc.currentQty,
        current_rate: calc.currentRate > 0 ? calc.currentRate : Number(item.cost_rate || item.standard_rate || 0),
        current_value: calc.currentValue,
        updated_at: now,
      })
      .eq("id", itemId);

    return calc;
  } catch (err) {
    console.error("[InventoryEngine] Error syncing item stock to DB:", err);
    return null;
  }
}

/**
 * Creates stock movements for a voucher and updates inventory item balances.
 * Strictly prevents duplicate entries using voucher ID + item ID + movement type.
 */
export async function postVoucherInventoryMovements(
  voucher: {
    id: string;
    voucherNumber: string;
    type: string;
    date: string;
    company_id?: string;
    companyId?: string;
    branch_id?: string;
    branchId?: string;
    status: string;
    party_name?: string;
    partyName?: string;
    items?: any[];
  },
  companyId: string,
  branchId?: string,
  userId?: string
): Promise<{ success: boolean; movementsCreated: number; errors?: string[] }> {
  // Only approved vouchers post physical inventory movements
  if (voucher.status !== "Approved") {
    return { success: true, movementsCreated: 0 };
  }

  const rawItems = Array.isArray(voucher.items)
    ? voucher.items
    : typeof voucher.items === "string"
    ? JSON.parse(voucher.items)
    : [];

  if (!rawItems || rawItems.length === 0) {
    return { success: true, movementsCreated: 0 };
  }

  // Determine movement direction based on voucher type
  let movementType: MovementType | null = null;
  const vType = voucher.type;

  if (vType === "Purchase" || vType === "Receipt Note") {
    movementType = "In";
  } else if (vType === "Sales" || vType === "Delivery Note") {
    movementType = "Out";
  } else if (vType === "Credit Note") {
    movementType = "Sales Return"; // Customer returns goods -> stock restored
  } else if (vType === "Debit Note") {
    movementType = "Purchase Return"; // Goods returned to supplier -> stock reduced
  }

  if (!movementType) {
    return { success: true, movementsCreated: 0 };
  }

  const compId = companyId || voucher.company_id || voucher.companyId;
  const brId = branchId || voucher.branch_id || voucher.branchId || null;
  const partyName = voucher.party_name || voucher.partyName || "Party";

  let createdCount = 0;
  const errors: string[] = [];

  for (const itm of rawItems) {
    // 1. Strict Service Isolation: Services NEVER create stock movements
    if (itm.itemType === "service" || itm.isService) {
      continue;
    }

    const itemId = itm.itemId || itm.stockItemId || itm.id;
    if (!itemId) continue;

    const qty = Number(itm.qty || itm.quantity || 0);
    const rate = Number(itm.rate || itm.unitPrice || 0);
    if (qty <= 0) continue;

    // 2. Strict Duplicate Prevention:
    // Check if movement already exists for this voucher + item + movement type
    const { data: existingMovs } = await supabase
      .from("stock_movements")
      .select("id, voucher_id, stock_item_id, type, quantity")
      .eq("voucher_id", voucher.id)
      .eq("stock_item_id", itemId)
      .eq("type", movementType);

    if (existingMovs && existingMovs.length > 0) {
      // Movement already exists for this voucher line, do not duplicate
      continue;
    }

    // Also check if movement was previously saved under reference = voucherNumber
    const { data: refMovs } = await supabase
      .from("stock_movements")
      .select("id, voucher_id, stock_item_id, type")
      .eq("reference", voucher.voucherNumber)
      .eq("stock_item_id", itemId)
      .eq("type", movementType);

    if (refMovs && refMovs.length > 0) {
      // Link the voucher_id to existing movement to preserve integrity without duplicating
      for (const rm of refMovs) {
        if (!rm.voucher_id) {
          await supabase
            .from("stock_movements")
            .update({ voucher_id: voucher.id, branch_id: brId })
            .eq("id", rm.id);
        }
      }
      await syncItemStockToDb(itemId, compId);
      continue;
    }

    // 3. Create fresh stock movement
    const movId = `mv-${crypto.randomUUID().slice(0, 8)}`;
    const now = new Date().toISOString();
    const narration = `${vType} #${voucher.voucherNumber} - ${partyName}`;

    const { error: insErr } = await supabase.from("stock_movements").insert({
      id: movId,
      company_id: compId,
      stock_item_id: itemId,
      voucher_id: voucher.id,
      voucher_line_id: itm.lineId || itm.line_id || null,
      branch_id: brId,
      warehouse: itm.warehouse || itm.location || null,
      batch_id: itm.batchId || itm.batch_id || null,
      type: movementType,
      date: voucher.date || now.split("T")[0],
      quantity: qty,
      rate: rate,
      value: Math.round(qty * rate * 100) / 100,
      reference: voucher.voucherNumber,
      narration: narration,
      created_by: userId || null,
    });

    if (insErr) {
      errors.push(`Failed to insert movement for item ${itemId}: ${insErr.message}`);
    } else {
      createdCount++;
      // 4. Update available stock and valuation in inventory_items
      await syncItemStockToDb(itemId, compId);
    }
  }

  return { success: errors.length === 0, movementsCreated: createdCount, errors };
}

/**
 * Reverses or removes stock movements when a voucher is cancelled, reversed, or deleted.
 */
export async function reverseVoucherInventoryMovements(
  voucherId: string,
  companyId: string
): Promise<{ success: boolean; reversedCount: number }> {
  try {
    // 1. Find all movements linked to this voucher
    const { data: movs, error: fetchErr } = await supabase
      .from("stock_movements")
      .select("id, stock_item_id")
      .eq("voucher_id", voucherId);

    if (fetchErr || !movs || movs.length === 0) {
      return { success: true, reversedCount: 0 };
    }

    const affectedItemIds: string[] = Array.from(new Set(movs.map((m: any) => String(m.stock_item_id))));

    // 2. Delete the voucher's movements
    await supabase.from("stock_movements").delete().eq("voucher_id", voucherId);

    // 3. Recalculate stock for all affected items
    for (const itemId of affectedItemIds) {
      await syncItemStockToDb(String(itemId), companyId);
    }

    return { success: true, reversedCount: movs.length };
  } catch (err) {
    console.error("[InventoryEngine] Error reversing voucher movements:", err);
    return { success: false, reversedCount: 0 };
  }
}

export interface ReconciliationPreview {
  success: boolean;
  companyId: string;
  isReconciled: boolean;
  summary: {
    totalApprovedVouchers: number;
    totalExistingCatalogueItems: number;
    missingCatalogueItemsCount: number;
    missingMovementsCount: number;
    unlinkedMovementsCount: number;
  };
  missingCatalogueItems: any[];
  missingMovements: any[];
  unlinkedMovements: any[];
  projectedStockChanges: any[];
}

export async function getReconciliationPreview(companyId: string): Promise<ReconciliationPreview> {
  const res = await fetch(`/api/inventory/reconcile-preview?companyId=${encodeURIComponent(companyId)}`, {
    headers: {
      "Content-Type": "application/json",
      "x-company-id": companyId,
    },
  });
  if (!res.ok) {
    throw new Error(`Failed to fetch preview: ${res.statusText}`);
  }
  return res.json();
}

export async function applyInventoryReconciliation(companyId: string): Promise<any> {
  const res = await fetch("/api/inventory/reconcile-apply", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "x-company-id": companyId,
    },
    body: JSON.stringify({ companyId }),
  });
  if (!res.ok) {
    throw new Error(`Failed to apply reconciliation: ${res.statusText}`);
  }
  return res.json();
}

/**
 * Comprehensive Idempotent Inventory Reconciliation Engine:
 * - Checks all approved vouchers with physical goods items
 * - Ensures each has an exact linked stock movement
 * - Recalculates current_qty and valuation for all items
 * - Never duplicates records
 */
export async function reconcileAllInventory(
  companyId: string
): Promise<{
  success: boolean;
  totalGoodsItems: number;
  totalServiceItems: number;
  movementsLinked: number;
  movementsCreated: number;
  itemsUpdated: { id: string; name: string; qty: number; value: number }[];
}> {
  try {
    const res = await fetch("/api/inventory/reconcile", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "x-company-id": companyId,
      },
      body: JSON.stringify({ companyId }),
    });

    if (res.ok) {
      const data = await res.json();
      if (data && data.success) {
        return {
          success: true,
          totalGoodsItems: data.totalGoodsItems || 0,
          totalServiceItems: data.totalServiceItems || 0,
          movementsLinked: data.movementsLinked || 0,
          movementsCreated: data.movementsCreated || 0,
          itemsUpdated: (data.itemsUpdated || []).map((i: any) => ({
            id: i.id,
            name: i.name,
            qty: i.currentQty ?? i.qty ?? 0,
            value: i.currentValue ?? i.value ?? 0,
          })),
        };
      }
    }
  } catch (apiErr) {
    console.warn("[InventoryEngine] Server reconciliation failed, falling back to client logic:", apiErr);
  }

  let movementsLinked = 0;
  let movementsCreated = 0;
  const itemsUpdated: { id: string; name: string; qty: number; value: number }[] = [];

  try {
    // 1. Fetch all inventory items
    const { data: allItems } = await supabase
      .from("inventory_items")
      .select("*")
      .eq("company_id", companyId);

    const items = allItems || [];
    const goodsItems = items.filter((i: any) => i.item_type !== "service");
    const serviceItems = items.filter((i: any) => i.item_type === "service");

    // 2. Fetch all approved vouchers
    const { data: vouchers } = await supabase
      .from("vouchers")
      .select("*")
      .eq("company_id", companyId)
      .eq("status", "Approved");

    // 3. Fetch all existing movements
    const { data: movements } = await supabase
      .from("stock_movements")
      .select("*")
      .eq("company_id", companyId);

    const existingMovements = movements || [];

    // 4. Verify each approved voucher with physical goods
    for (const v of vouchers || []) {
      const rawItems = Array.isArray(v.items)
        ? v.items
        : typeof v.items === "string"
        ? JSON.parse(v.items || "[]")
        : [];

      if (!rawItems || rawItems.length === 0) continue;

      let movType: MovementType | null = null;
      if (v.type === "Purchase" || v.type === "Receipt Note") movType = "In";
      else if (v.type === "Sales" || v.type === "Delivery Note") movType = "Out";
      else if (v.type === "Credit Note") movType = "Sales Return";
      else if (v.type === "Debit Note") movType = "Purchase Return";

      if (!movType) continue;

      for (const itm of rawItems) {
        if (itm.itemType === "service" || itm.isService) continue;

        const matchedItem = goodsItems.find(
          (gi: any) =>
            gi.id === itm.id ||
            gi.id === itm.itemId ||
            gi.name.trim().toLowerCase() === (itm.name || itm.description || "").trim().toLowerCase()
        );

        if (!matchedItem) continue;

        const qty = Number(itm.qty || itm.quantity || 0);
        const rate = Number(itm.rate || itm.unitPrice || 0);
        if (qty <= 0) continue;

        // Check if movement linked to this voucher already exists
        const linkedMov = existingMovements.find(
          (m: any) =>
            m.voucher_id === v.id &&
            m.stock_item_id === matchedItem.id &&
            m.type === movType
        );

        if (linkedMov) {
          // Already linked properly
          continue;
        }

        // Check if unlinked movement exists with reference = v.voucherNumber
        const unlinkedMov = existingMovements.find(
          (m: any) =>
            (m.reference === v.voucherNumber || m.reference === v.invoice_number) &&
            m.stock_item_id === matchedItem.id &&
            m.type === movType
        );

        if (unlinkedMov) {
          // Safely link the existing movement rather than creating a duplicate
          await supabase
            .from("stock_movements")
            .update({
              voucher_id: v.id,
              branch_id: v.branch_id || matchedItem.branch_id || null,
            })
            .eq("id", unlinkedMov.id);

          unlinkedMov.voucher_id = v.id;
          movementsLinked++;
        } else {
          // Movement is genuinely missing -> create it
          const newMovId = `mv-${crypto.randomUUID().slice(0, 8)}`;
          const now = new Date().toISOString();
          await supabase.from("stock_movements").insert({
            id: newMovId,
            company_id: companyId,
            stock_item_id: matchedItem.id,
            voucher_id: v.id,
            voucher_line_id: itm.lineId || null,
            branch_id: v.branch_id || null,
            type: movType,
            date: v.date || now.split("T")[0],
            quantity: qty,
            rate: rate,
            value: Math.round(qty * rate * 100) / 100,
            reference: v.voucherNumber,
            narration: `${v.type} #${v.voucherNumber} - ${v.party_name || "Party"}`,
            created_by: v.created_by || "system",
          });

          existingMovements.push({
            id: newMovId,
            company_id: companyId,
            stock_item_id: matchedItem.id,
            voucher_id: v.id,
            type: movType,
            quantity: qty,
            rate: rate,
          });
          movementsCreated++;
        }
      }
    }

    // 5. Recalculate and persist every physical goods item
    for (const gi of goodsItems) {
      const calc = await syncItemStockToDb(gi.id, companyId);
      if (calc) {
        itemsUpdated.push({
          id: gi.id,
          name: gi.name,
          qty: calc.currentQty,
          value: calc.currentValue,
        });
      }
    }

    // 6. Enforce zero stock for all service items
    for (const si of serviceItems) {
      await supabase
        .from("inventory_items")
        .update({
          opening_qty: 0,
          current_qty: 0,
          opening_value: 0,
          current_value: 0,
          reorder_level: null,
          updated_at: new Date().toISOString(),
        })
        .eq("id", si.id);
    }

    return {
      success: true,
      totalGoodsItems: goodsItems.length,
      totalServiceItems: serviceItems.length,
      movementsLinked,
      movementsCreated,
      itemsUpdated,
    };
  } catch (err) {
    console.error("[InventoryEngine] Error during reconciliation:", err);
    return {
      success: false,
      totalGoodsItems: 0,
      totalServiceItems: 0,
      movementsLinked,
      movementsCreated,
      itemsUpdated,
    };
  }
}
